// TS port of apk/lib/services/interaction_scanner.dart. Same taxonomy,
// same rules, same severity ordering — keep these in lockstep across both
// clients. When the Dart version changes, mirror here.

import { COMPOUND_BY_ID, type Compound } from './compounds';
import type { StackItem } from './stack_store';

export type Severity = 'serious' | 'caution' | 'info';

export type InteractionWarning = {
  severity: Severity;
  aId: string;
  bId: string;
  aName: string;
  bName: string;
  shortLabel: string;
  reason: string;
};

export const SCANNER_DISCLAIMER =
  "Not medical advice. This scanner catches known interaction patterns, not " +
  "every possible combination. Product quality varies. We endorse no specific " +
  "stack and recommend you discuss any new combination with your physician or " +
  "pharmacist — especially if you're on prescription medication, pregnant or " +
  "nursing, have liver/kidney/thyroid conditions, or are about to have surgery.";

const COMPOUND_CLASSES: Record<string, string[]> = {
  '5-htp':              ['serotonergic', 'ssri', 'snri', 'MAO_inhibitors'],
  'l-tryptophan':       ['serotonergic', 'ssri', 'snri', 'MAO_inhibitors'],
  'st-johns-wort':      ['serotonergic', 'ssri', 'MAO_inhibitors', 'cyp3a4_inducer'],
  'same':               ['serotonergic', 'ssri', 'MAO_inhibitors'],
  'saffron':            ['serotonergic'],
  'mucuna-pruriens':    ['MAO_inhibitors', 'levodopa', 'serotonergic'],
  'caffeine':           ['stimulants'],
  'theacrine':          ['stimulants'],
  'theobromine':        ['stimulants'],
  'yohimbine':          ['stimulants', 'MAO_inhibitors'],
  'synephrine':         ['stimulants', 'MAO_inhibitors'],
  'sulbutiamine':       ['stimulants'],
  'modafinil-info':     ['stimulants'],
  'pea-phenethylamine': ['stimulants', 'MAO_inhibitors'],
  '5-hour-energy':      ['stimulants'],
  'zipfizz':            ['stimulants'],
  'c4-preworkout':      ['stimulants'],
  'pre-jym':            ['stimulants'],
  'pre-kaged':          ['stimulants'],
  'bucked-up':          ['stimulants'],
  'ghost-legend':       ['stimulants'],
  'four-sigmatic-coffee':['stimulants'],
  'green-tea-extract':  ['stimulants'],
  'phenylpiracetam-info':['stimulants'],
  'gaba':               ['sedatives'],
  'valerian':           ['sedatives'],
  'passionflower':      ['sedatives'],
  'lemon-balm':         ['sedatives', 'thyroid_active'],
  'chamomile':          ['sedatives'],
  'magnolia-bark':      ['sedatives'],
  'skullcap':           ['sedatives'],
  'hops':               ['sedatives'],
  'apigenin':           ['sedatives'],
  'cbd':                ['sedatives', 'cyp3a4_inhibitor'],
  'cbn':                ['sedatives'],
  'melatonin':          ['sedatives'],
  'kratom-info':        ['sedatives', 'opioids', 'MAO_inhibitors'],
  'zzzquil-info':       ['sedatives', 'anticholinergic_medications'],
  'lavender-oral':      ['sedatives'],
  'omega-3':            ['anticoagulants_mild'],
  'vitamin-e':          ['anticoagulants_mild'],
  'ginger':             ['anticoagulants_mild'],
  'turmeric':           ['anticoagulants_mild'],
  'longvida-curcumin':  ['anticoagulants_mild'],
  'fish-oil':           ['anticoagulants_mild'],
  'ginkgo-biloba':      ['anticoagulants_mild'],
  'garlic':             ['anticoagulants_mild'],
  'aged-garlic':        ['anticoagulants_mild'],
  'bromelain':          ['anticoagulants_mild'],
  'boswellia':          ['anticoagulants_mild'],
  'grape-seed-extract': ['anticoagulants_mild'],
  'pycnogenol':         ['anticoagulants_mild'],
  'beet-root':          ['antihypertensives_mild'],
  'citrulline-malate':  ['antihypertensives_mild'],
  'l-arginine':         ['antihypertensives_mild'],
  'l-citrulline':       ['antihypertensives_mild'],
  'olive-leaf':         ['antihypertensives_mild'],
  'pomegranate-extract':['antihypertensives_mild'],
  'hawthorn':           ['antihypertensives_mild'],
  'taurine':            ['antihypertensives_mild'],
  'magnesium-glycinate':['antihypertensives_mild'],
  'magnesium-citrate':  ['antihypertensives_mild'],
  'magnesium-malate':   ['antihypertensives_mild'],
  'magnesium-taurate':  ['antihypertensives_mild'],
  'forskolin':          ['antihypertensives_mild'],
  'berberine':          ['antidiabetics_mild'],
  'cinnamon':           ['antidiabetics_mild'],
  'gymnema':            ['antidiabetics_mild'],
  'fenugreek':          ['antidiabetics_mild'],
  'bitter-melon':       ['antidiabetics_mild'],
  'alpha-lipoic-acid':  ['antidiabetics_mild'],
  'chromium':           ['antidiabetics_mild'],
  'apple-cider-vinegar':['antidiabetics_mild'],
  'iodine':             ['thyroid_active'],
  'sea-moss':           ['thyroid_active'],
  'ashwagandha':        ['thyroid_active'],
  'echinacea':          ['immune_stimulating'],
  'astragalus':         ['immune_stimulating'],
  'elderberry':         ['immune_stimulating'],
  'beta-glucan':        ['immune_stimulating'],
  'cordyceps':          ['immune_stimulating'],
  'reishi':             ['immune_stimulating'],
  'turkey-tail':        ['immune_stimulating'],
  'maitake':            ['immune_stimulating'],
  'berberine_cyp':      ['cyp3a4_inhibitor'],
  'huperzine-a':        ['acetylcholinesterase_inhibitor'],
};

type Rule = {
  classA: string;
  triggerB: string;
  severity: Severity;
  label: string;
  reason: string;
};

const RULES: Rule[] = [
  { classA: 'serotonergic', triggerB: 'ssri',                       severity: 'serious', label: 'Serotonin syndrome risk',
    reason: 'Combining serotonin-precursor compounds with SSRIs, SNRIs, or MAOIs can cause serotonin syndrome — fever, agitation, tremor, sweating. Do not stack without prescriber supervision.' },
  { classA: 'serotonergic', triggerB: 'MAO_inhibitors',             severity: 'serious', label: 'Serotonin syndrome risk',
    reason: 'Combining serotonin-precursor compounds with an MAOI can cause serotonin syndrome and hypertensive crisis. Avoid this combination.' },
  { classA: 'serotonergic', triggerB: 'serotonergic',               severity: 'serious', label: 'Serotonin overload risk',
    reason: 'Two serotonin-precursor compounds in one stack increases the risk of serotonin overload, especially with SSRIs/SNRIs/MAOIs.' },
  { classA: 'stimulants',   triggerB: 'MAO_inhibitors',             severity: 'serious', label: 'Hypertensive crisis risk',
    reason: 'Stimulants taken alongside MAOIs (including yohimbine, synephrine) can cause sudden severe high blood pressure.' },
  { classA: 'opioids',      triggerB: 'sedatives',                  severity: 'serious', label: 'Respiratory depression risk',
    reason: 'Combining opioid-like compounds with sedatives can depress breathing dangerously.' },
  { classA: 'stimulants',   triggerB: 'stimulants',                 severity: 'caution', label: 'Stimulant stacking',
    reason: 'Two stimulants together (e.g., caffeine + yohimbine, caffeine + synephrine) increase heart rate and anxiety risk. Watch dose.' },
  { classA: 'sedatives',    triggerB: 'sedatives',                  severity: 'caution', label: 'Additive sedation',
    reason: 'Multiple sedative compounds combine to deepen drowsiness and motor impairment. Do not drive.' },
  { classA: 'anticoagulants_mild', triggerB: 'anticoagulants_mild', severity: 'caution', label: 'Additive bleeding risk',
    reason: 'Several mild blood-thinning supplements stacked together can meaningfully raise bleeding risk, especially before surgery or with NSAIDs/aspirin.' },
  { classA: 'anticoagulants_mild', triggerB: 'anticoagulants',      severity: 'caution', label: 'Bleeding risk with anticoagulant medication',
    reason: 'This compound has blood-thinning activity. If you take warfarin, DOACs, aspirin, or other anticoagulants, this stack can raise bleeding risk.' },
  { classA: 'antihypertensives_mild', triggerB: 'antihypertensives_mild', severity: 'caution', label: 'Additive blood-pressure drop',
    reason: 'Stacking multiple blood-pressure-lowering supplements can cause unwanted hypotension (dizziness, fainting).' },
  { classA: 'antidiabetics_mild', triggerB: 'antidiabetics_mild',   severity: 'caution', label: 'Additive hypoglycemia risk',
    reason: 'Several blood-sugar-lowering supplements stacked can drop glucose more than expected, especially if you also take antidiabetic medication.' },
  { classA: 'thyroid_active', triggerB: 'thyroid_medications',      severity: 'caution', label: 'Thyroid interaction',
    reason: 'This compound affects thyroid function. If you take levothyroxine or other thyroid meds, get labs before stacking.' },
  { classA: 'immune_stimulating', triggerB: 'immunosuppressants',   severity: 'caution', label: 'Immune-modulating conflict',
    reason: 'Immune-stimulating compounds can interfere with immunosuppressant medications (post-transplant, autoimmune).' },
  { classA: 'acetylcholinesterase_inhibitor', triggerB: 'anticholinergic_medications', severity: 'caution', label: 'Cholinergic conflict',
    reason: 'This compound increases acetylcholine; pairing with anticholinergic drugs cancels both and can have unpredictable effects.' },
];

function interactionsOf(c: Compound): string[] {
  return [
    ...(c.interactions || []),
    ...((COMPOUND_CLASSES[c.id] || []) as string[]),
  ];
}

const SEVERITY_RANK: Record<Severity, number> = { serious: 0, caution: 1, info: 2 };

function pairKey(w: InteractionWarning): string {
  const pair = [w.aId, w.bId].sort();
  return `${pair[0]}::${pair[1]}::${w.shortLabel}`;
}

export function scanStack(activeItems: StackItem[]): InteractionWarning[] {
  const ids = Array.from(new Set(activeItems.filter((i) => i.active).map((i) => i.compound_id)));
  const out: InteractionWarning[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < ids.length; i++) {
    const aId = ids[i];
    const aC = COMPOUND_BY_ID[aId];
    if (!aC) continue;
    const aClasses = COMPOUND_CLASSES[aId] || [];
    const aInter = interactionsOf(aC);
    for (let j = i + 1; j < ids.length; j++) {
      const bId = ids[j];
      const bC = COMPOUND_BY_ID[bId];
      if (!bC) continue;
      const bClasses = COMPOUND_CLASSES[bId] || [];
      const bInter = interactionsOf(bC);
      for (const rule of RULES) {
        const hitAB = aClasses.includes(rule.classA) &&
          (bClasses.includes(rule.classA) || bClasses.includes(rule.triggerB) || bInter.includes(rule.triggerB));
        const hitBA = bClasses.includes(rule.classA) &&
          (aClasses.includes(rule.classA) || aClasses.includes(rule.triggerB) || aInter.includes(rule.triggerB));
        if (!hitAB && !hitBA) continue;
        const w: InteractionWarning = {
          severity: rule.severity,
          aId, bId,
          aName: aC.display_name, bName: bC.display_name,
          shortLabel: rule.label,
          reason: rule.reason,
        };
        const k = pairKey(w);
        if (!seen.has(k)) { seen.add(k); out.push(w); }
      }
    }
  }
  out.sort((a, b) => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    return a.shortLabel.localeCompare(b.shortLabel);
  });
  return out;
}

export function worstSeverity(warnings: InteractionWarning[]): Severity | null {
  return warnings.length === 0 ? null : warnings[0].severity;
}
